import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { syncTierToMarianaTek } from '../services/api'

const Container = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1.5rem;
`

const Description = styled.p`
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  margin-bottom: 2rem;
  line-height: 1.6;
`

const Card = styled.div`
  background: ${theme.color.background.frame};
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px ${theme.color.shadow.light};
`

const UploadArea = styled.div<{ isDragOver?: boolean }>`
  border: 2px dashed ${props => props.isDragOver ? theme.color.accent.primary : theme.color.border.light};
  border-radius: 8px;
  padding: 3rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.isDragOver ? 'rgba(200, 16, 46, 0.05)' : 'transparent'};

  &:hover {
    border-color: ${theme.color.accent.primary};
    background: rgba(200, 16, 46, 0.02);
  }
`

const UploadIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`

const UploadText = styled.div`
  font-size: 1.6rem;
  color: ${theme.color.text.base};
  margin-bottom: 0.5rem;
`

const UploadSubtext = styled.div`
  font-size: 1.3rem;
  color: ${theme.color.text.grey};
`

const HiddenInput = styled.input`
  display: none;
`

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  background: ${props => {
    switch (props.variant) {
      case 'secondary': return theme.color.background.frame;
      case 'danger': return '#c62828';
      default: return theme.color.accent.primary;
    }
  }};
  color: ${props => props.variant === 'secondary' ? theme.color.text.base : 'white'};
  border: ${props => props.variant === 'secondary' ? `1px solid ${theme.color.border.light}` : 'none'};
  border-radius: 4px;
  padding: 1rem 2rem;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 1rem;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    background: ${theme.color.text.grey};
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const FileInfo = styled.div`
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 4px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FileName = styled.span`
  font-size: 1.4rem;
  color: ${theme.color.text.base};
  font-weight: 500;
`

const FileStats = styled.span`
  font-size: 1.3rem;
  color: ${theme.color.text.grey};
`

const ProgressContainer = styled.div`
  margin-top: 2rem;
`

const ProgressBar = styled.div`
  background: ${theme.color.border.light};
  border-radius: 4px;
  height: 8px;
  overflow: hidden;
  margin-bottom: 1rem;
`

const ProgressFill = styled.div<{ progress: number }>`
  background: ${theme.color.accent.primary};
  height: 100%;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`

const ProgressText = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.base};
  text-align: center;
`

const LogContainer = styled.div`
  background: #1a1a1a;
  border-radius: 4px;
  padding: 1rem;
  max-height: 300px;
  overflow-y: auto;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 1.2rem;
  margin-top: 1.5rem;
`

const LogEntry = styled.div<{ type: 'info' | 'success' | 'error' | 'warning' }>`
  color: ${props => {
    switch (props.type) {
      case 'success': return '#4caf50';
      case 'error': return '#f44336';
      case 'warning': return '#ff9800';
      default: return '#90caf9';
    }
  }};
  padding: 0.3rem 0;
  word-break: break-all;
`

const SummaryCard = styled.div<{ type: 'success' | 'error' | 'info' }>`
  background: ${props => {
    switch (props.type) {
      case 'success': return '#e8f5e9';
      case 'error': return '#ffebee';
      default: return '#e3f2fd';
    }
  }};
  border: 1px solid ${props => {
    switch (props.type) {
      case 'success': return '#a5d6a7';
      case 'error': return '#ef9a9a';
      default: return '#90caf9';
    }
  }};
  border-radius: 4px;
  padding: 1.5rem;
  margin-top: 1.5rem;
`

const SummaryTitle = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`

const SummaryText = styled.div`
  font-size: 1.4rem;
`

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  font-size: 1.3rem;
`

const PreviewTh = styled.th`
  background: ${theme.color.background.light};
  padding: 0.8rem;
  text-align: left;
  border-bottom: 2px solid ${theme.color.border.light};
  font-weight: 600;
`

const PreviewTd = styled.td`
  padding: 0.8rem;
  border-bottom: 1px solid ${theme.color.border.light};
`

interface TierMismatch {
  email: string
  sessionm_id: string
  sessionm_tier_level_name: string
}

interface LogEntry {
  timestamp: Date
  type: 'info' | 'success' | 'error' | 'warning'
  message: string
}

const TierTagSync: React.FC = () => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [parsedData, setParsedData] = useState<TierMismatch[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [processedCount, setProcessedCount] = useState(0)
  const [successCount, setSuccessCount] = useState(0)
  const [errorCount, setErrorCount] = useState(0)
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [isComplete, setIsComplete] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addLog = (type: LogEntry['type'], message: string) => {
    setLogs(prev => [...prev, { timestamp: new Date(), type, message }])
  }

  const parseCSV = (text: string): TierMismatch[] => {
    const lines = text.trim().split('\n')
    if (lines.length < 2) {
      throw new Error('CSV file must have a header row and at least one data row')
    }

    const header = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''))
    const emailIndex = header.findIndex(h => h === 'email')
    const sessionmIdIndex = header.findIndex(h => h === 'sessionm_id')
    const tierNameIndex = header.findIndex(h => h === 'sessionm_tier_level_name')

    if (emailIndex === -1 || sessionmIdIndex === -1 || tierNameIndex === -1) {
      throw new Error('CSV must contain "email", "sessionm_id", and "sessionm_tier_level_name" columns')
    }

    const data: TierMismatch[] = []
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue

      const values = line.split(',').map(v => v.trim().replace(/"/g, ''))
      const email = values[emailIndex]
      const sessionm_id = values[sessionmIdIndex]
      const sessionm_tier_level_name = values[tierNameIndex]

      if (email && sessionm_id && sessionm_tier_level_name) {
        data.push({ email, sessionm_id, sessionm_tier_level_name })
      }
    }

    return data
  }

  const handleFile = async (selectedFile: File) => {
    setFile(selectedFile)
    setParsedData([])
    setLogs([])
    setIsComplete(false)
    setProgress(0)
    setProcessedCount(0)
    setSuccessCount(0)
    setErrorCount(0)

    try {
      const text = await selectedFile.text()
      const data = parseCSV(text)
      setParsedData(data)
      addLog('info', `Parsed ${data.length} records from CSV`)
    } catch (error) {
      addLog('error', error instanceof Error ? error.message : 'Failed to parse CSV')
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      handleFile(droppedFile)
    } else {
      addLog('error', 'Please drop a CSV file')
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      handleFile(selectedFile)
    }
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleClear = () => {
    setFile(null)
    setParsedData([])
    setLogs([])
    setIsComplete(false)
    setProgress(0)
    setProcessedCount(0)
    setSuccessCount(0)
    setErrorCount(0)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleProcess = async () => {
    if (parsedData.length === 0) return

    setIsProcessing(true)
    setProgress(0)
    setProcessedCount(0)
    setSuccessCount(0)
    setErrorCount(0)
    setIsComplete(false)

    addLog('info', `Starting tier tag sync for ${parsedData.length} users...`)

    let successTotal = 0
    let errorTotal = 0

    for (let i = 0; i < parsedData.length; i++) {
      const record = parsedData[i]

      try {
        await syncTierToMarianaTek(record.email, record.sessionm_tier_level_name)
        successTotal++
        addLog('success', `[${i + 1}/${parsedData.length}] ✓ Synced ${record.sessionm_id} to tier "${record.sessionm_tier_level_name}"`)
      } catch (error) {
        errorTotal++
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        addLog('error', `[${i + 1}/${parsedData.length}] ✗ Failed ${record.sessionm_id}: ${errorMessage}`)
      }

      setProcessedCount(i + 1)
      setSuccessCount(successTotal)
      setErrorCount(errorTotal)
      setProgress(Math.round(((i + 1) / parsedData.length) * 100))

      // Small delay to prevent overwhelming the API
      await new Promise(resolve => setTimeout(resolve, 100))
    }

    setIsProcessing(false)
    setIsComplete(true)
    addLog('info', `Sync complete. Success: ${successTotal}, Errors: ${errorTotal}`)
  }

  return (
    <Container>
      <Title>Tier Tag Sync</Title>
      <Description>
        Upload a CSV file containing users with mismatched tier tags. The CSV must have columns:
        <strong> email</strong>, <strong>sessionm_id</strong>, and <strong>sessionm_tier_level_name</strong>.
        This tool will sync each user's tier tag in Mariana Tek to match their SessionM tier.
      </Description>

      <Card>
        {!file ? (
          <UploadArea
            isDragOver={isDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleUploadClick}
          >
            <UploadIcon>📁</UploadIcon>
            <UploadText>Drop CSV file here or click to browse</UploadText>
            <UploadSubtext>Accepts .csv files with tier mismatch data</UploadSubtext>
            <HiddenInput
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
            />
          </UploadArea>
        ) : (
          <>
            <FileInfo>
              <FileName>📄 {file.name}</FileName>
              <FileStats>{parsedData.length} records</FileStats>
            </FileInfo>

            {parsedData.length > 0 && (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Preview (first 5 rows):</strong>
                </div>
                <PreviewTable>
                  <thead>
                    <tr>
                      <PreviewTh>SessionM ID</PreviewTh>
                      <PreviewTh>Tier Level Name</PreviewTh>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 5).map((row, idx) => (
                      <tr key={idx}>
                        <PreviewTd>{row.sessionm_id}</PreviewTd>
                        <PreviewTd>{row.sessionm_tier_level_name}</PreviewTd>
                      </tr>
                    ))}
                  </tbody>
                </PreviewTable>
                {parsedData.length > 5 && (
                  <div style={{ marginTop: '0.5rem', color: theme.color.text.grey, fontSize: '1.3rem' }}>
                    ... and {parsedData.length - 5} more rows
                  </div>
                )}
              </>
            )}

            <div style={{ marginTop: '1.5rem' }}>
              <Button
                onClick={handleProcess}
                disabled={isProcessing || parsedData.length === 0}
              >
                {isProcessing ? 'Processing...' : `Sync ${parsedData.length} Users`}
              </Button>
              <Button variant="secondary" onClick={handleClear} disabled={isProcessing}>
                Clear
              </Button>
            </div>

            {isProcessing && (
              <ProgressContainer>
                <ProgressBar>
                  <ProgressFill progress={progress} />
                </ProgressBar>
                <ProgressText>
                  {processedCount} / {parsedData.length} ({progress}%)
                </ProgressText>
              </ProgressContainer>
            )}

            {isComplete && (
              <SummaryCard type={errorCount === 0 ? 'success' : errorCount === parsedData.length ? 'error' : 'info'}>
                <SummaryTitle>Sync Complete</SummaryTitle>
                <SummaryText>
                  ✓ Success: {successCount} | ✗ Errors: {errorCount} | Total: {parsedData.length}
                </SummaryText>
              </SummaryCard>
            )}

            {logs.length > 0 && (
              <LogContainer>
                {logs.map((log, idx) => (
                  <LogEntry key={idx} type={log.type}>
                    [{log.timestamp.toLocaleTimeString()}] {log.message}
                  </LogEntry>
                ))}
              </LogContainer>
            )}
          </>
        )}
      </Card>
    </Container>
  )
}

export default TierTagSync
